// VideoTutorialProvider.tsx
import React, { createContext, useContext, useState, ReactNode } from 'react';
import { Dialog, DialogContent, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CustomDialog from '../../components/common/custom-dialog/custom-dialog';

interface VideoTutorialContextType {
  showVideoTutorial: (videoUrl: string) => void;
}

const VideoTutorialContext = createContext<VideoTutorialContextType | undefined>(undefined);

export const useVideoTutorial = () => {
  const context = useContext(VideoTutorialContext);
  if (!context) {
    throw new Error('useVideoTutorial must be used within a VideoTutorialProvider');
  }
  return context;
};

interface VideoTutorialProviderProps {
  children: ReactNode;
}

export const VideoTutorialProvider: React.FC<VideoTutorialProviderProps> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');

  const showVideoTutorial = (url: string) => {
    setVideoUrl(url);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setVideoUrl('');
  };

  return (
    <VideoTutorialContext.Provider value={{ showVideoTutorial }}>
      {children}
      <CustomDialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogContent sx={{ position: 'relative', padding: 0 }}>
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{ position: 'absolute', right: 8, top: 8, zIndex: 1 }}
          >
            <CloseIcon />
          </IconButton>
          {open && (
            <iframe
              width="100%"
              height="500"
              src={`${videoUrl}?autoplay=1`}
              title="YouTube video tutorial"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          )}
        </DialogContent>
      </CustomDialog>
    </VideoTutorialContext.Provider>
  );
};
